import { App as CapacitorApp } from "@capacitor/app";
import type { PluginListenerHandle } from "@capacitor/core";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { isNativeApp } from "@/services/mobileAuth";

const supportedPaths = [
  /^\/$/,
  /^\/login$/,
  /^\/register$/,
  /^\/forgot-password$/,
  /^\/reset-password$/,
  /^\/verify-email$/,
  /^\/dashboard$/,
  /^\/books(?:\/.*)?$/,
  /^\/reading$/,
  /^\/stats$/,
  /^\/settings$/,
  /^\/discover$/,
  /^\/community$/,
];

export function routeFromAppUrl(
  rawUrl: string,
  appLinkHost: string = import.meta.env.VITE_APP_LINK_HOST || ""
): string | null {
  try {
    const url = new URL(rawUrl);
    let pathname: string;
    if (url.protocol === "https:") {
      if (!appLinkHost || url.hostname !== appLinkHost) return null;
      pathname = url.pathname;
    } else if (url.protocol === "com.celsus.app:") {
      pathname = url.pathname || `/${url.hostname}`;
    } else {
      return null;
    }
    if (!supportedPaths.some((pattern) => pattern.test(pathname))) return null;
    return `${pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function NativeAppBridge(): null {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNativeApp()) return;
    let disposed = false;
    let handles: PluginListenerHandle[] = [];
    const openRoute = (url: string): void => {
      const route = routeFromAppUrl(url);
      if (route) navigate(route);
    };

    void Promise.all([
      CapacitorApp.addListener("appUrlOpen", ({ url }) => openRoute(url)),
      CapacitorApp.addListener("backButton", ({ canGoBack }) => {
        if (canGoBack) navigate(-1);
        else void CapacitorApp.exitApp();
      }),
    ]).then((listenerHandles) => {
      if (disposed) {
        for (const handle of listenerHandles) void handle.remove();
      } else {
        handles = listenerHandles;
      }
    });
    void CapacitorApp.getLaunchUrl().then((launch) => {
      if (!disposed && launch?.url) openRoute(launch.url);
    });

    return () => {
      disposed = true;
      for (const handle of handles) void handle.remove();
    };
  }, [navigate]);

  return null;
}
